/**
 * Credit Handlers — CREDIT_SUMMARY, CREDIT_UPDATE, CREDIT_WARNING, CREDIT_EXHAUSTED, CREDIT_APPROVAL_NEEDED
 *
 * Listens for credit-related socket events and updates the credit store.
 * CREDIT_SUMMARY is pushed on socket connect — replaces the old HTTP fetchCredits() flow.
 * CREDIT_APPROVAL_NEEDED pushes an inline CreditApprovalBlock into the task's streaming message.
 */

import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import { useCreditStore } from "../creditStore";
import { useTaskStore } from "../taskStore";
import { enqueueMainPending } from "../pendingQueue";
import type {
  CreditUpdatePayload,
  CreditWarningPayload,
  CreditExhaustedPayload,
  CreditApprovalNeededPayload,
  CreditApprovalBlock,
  CreditSummary,
} from "@bash/shared";

export function registerCreditHandlers(socket: Socket): () => void {
  const creditStore = useCreditStore;
  const taskStore = useTaskStore;

  const handleCreditSummary = (data: CreditSummary) => {
    creditStore.setState({
      balance: data.balance,
      lifetimeSpent: data.lifetimeSpent,
      lifetimeEarned: data.lifetimeEarned,
      lastRechargeAmount: data.lastRechargeAmount ?? 0,
      spentSinceRecharge: data.spentSinceRecharge ?? 0,
      priorBalance: data.priorBalance ?? 0,
      todaySpent: data.todaySpent,
      weekSpent: data.weekSpent,
      weekByDay: data.weekByDay,
      todayIntelligence: data.todayIntelligence ?? 0,
      todayActions: data.todayActions ?? 0,
      todayCompute: data.todayCompute ?? 0,
      weekIntelligence: data.weekIntelligence ?? 0,
      weekActions: data.weekActions ?? 0,
      weekCompute: data.weekCompute ?? 0,
      allTimeIntelligence: data.allTimeIntelligence ?? 0,
      allTimeActions: data.allTimeActions ?? 0,
      allTimeCompute: data.allTimeCompute ?? 0,
      allTimeOther: data.allTimeOther ?? 0,
      composerTyped: data.composerTyped ?? 0,
      composerVoice: data.composerVoice ?? 0,
      isGracePeriod: data.isGracePeriod,
      graceExpiresAt: data.graceExpiresAt
        ? new Date(data.graceExpiresAt)
        : null,
      settings: {
        warnBalance: data.settings.warnBalance,
        confirmToolCost: data.settings.confirmToolCost,
        confirmTaskCost: data.settings.confirmTaskCost,
        // This push REPLACES `settings` wholesale, so an omitted ceiling would
        // blank the Settings field mid-edit on the next summary tick.
        turnSpendCeilingUsd: data.settings.turnSpendCeilingUsd ?? null,
        dailySpendCeilingUsd: data.settings.dailySpendCeilingUsd ?? null,
        maxTaskBudgetUsd: data.settings.maxTaskBudgetUsd ?? null,
      },
      isLoaded: true,
    });
  };

  const handleCreditUpdate = (data: CreditUpdatePayload) => {
    creditStore.getState().handleCreditUpdate(data);
  };

  const handleCreditWarning = (data: CreditWarningPayload) => {
    creditStore.getState().handleCreditWarning(data);
  };

  const handleCreditExhausted = (data: CreditExhaustedPayload) => {
    creditStore.getState().handleCreditExhausted(data);
  };

  const handleCreditApprovalNeeded = (data: CreditApprovalNeededPayload) => {
    // Push an inline CreditApprovalBlock into the task's streaming message
    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(data.taskId);
      if (!task) return;

      // Ensure streaming message exists
      if (!task.streamingMessage) {
        const clientId = `msg-credit-${data.requestId}`;
        task.streamingMessage = {
          id: clientId,
          role: "assistant",
          content: [],
          timestamp: new Date(),
          agentSlug: task.agentSlug || "bash",
          identity: { kind: "optimistic", clientId },
        };
      }

      const block: CreditApprovalBlock = {
        type: "credit_approval",
        requestId: data.requestId,
        toolName: data.toolName,
        displayName: data.displayName,
        estimatedCost: data.estimatedCost,
        currentBalance: data.currentBalance,
        status: "pending",
      };

      task.streamingMessage.content.push(block);
      enqueueMainPending(task, {
        type: "approval",
        toolName: data.displayName || data.toolName,
        requestId: data.requestId,
        toolUseId: "",
        agentSlug: task.agentSlug || "bash",
        taskId: data.taskId,
      });
      task.phase = "idle";
      // last_activity_at is now bumped by the Postgres trigger on the
      // server-side awaiting transition (setTaskAwaitingCredit). Realtime
      // delivers the value to taskStreamHandlers; no client-side bump needed.
    });
  };

  // Server fires this when its Realtime CREDIT_SUMMARY emit failed (e.g.
  // transient DB error in getUserCreditSummary). Refetch via HTTP so the
  // UI doesn't stay stuck on a stale balance.
  const handleCreditSummaryStale = () => {
    creditStore.getState().fetchCredits();
  };

  socket.on(EVENTS.CREDIT_SUMMARY, handleCreditSummary);
  socket.on(EVENTS.CREDIT_SUMMARY_STALE, handleCreditSummaryStale);
  socket.on(EVENTS.CREDIT_UPDATE, handleCreditUpdate);
  socket.on(EVENTS.CREDIT_WARNING, handleCreditWarning);
  socket.on(EVENTS.CREDIT_EXHAUSTED, handleCreditExhausted);
  socket.on(EVENTS.CREDIT_APPROVAL_NEEDED, handleCreditApprovalNeeded);

  return () => {
    socket.off(EVENTS.CREDIT_SUMMARY, handleCreditSummary);
    socket.off(EVENTS.CREDIT_SUMMARY_STALE, handleCreditSummaryStale);
    socket.off(EVENTS.CREDIT_UPDATE, handleCreditUpdate);
    socket.off(EVENTS.CREDIT_WARNING, handleCreditWarning);
    socket.off(EVENTS.CREDIT_EXHAUSTED, handleCreditExhausted);
    socket.off(EVENTS.CREDIT_APPROVAL_NEEDED, handleCreditApprovalNeeded);
  };
}
